import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  isPartnerWebviewSession,
  partnerHomePath,
} from "utils/partnerSession.util";
import { goBack } from "utils/navigation.util";
import "./PageHeader.scss";

/**
 * Back button and title, for the screens that are not part of the booking
 * flow — terms, privacy and anything else reached from Support.
 *
 * Inside the webview there is no browser chrome, so a screen without a back
 * control is a dead end: opening the terms from Support trapped you there
 * with no way out but killing the app. The same back button as the booking
 * flow, so every screen in the app carries one header design.
 */
export default function PageHeader({ title }: { title: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation("translation");

  /* history.back() where there is an in-app screen to go back to, home
     otherwise — a direct load (a link out of an email, a deep link from the
     native shell) has nothing behind it, and back would leave the webview.
     (history.length counted entries from before the app too, so it was
     almost never 1.) */
  const handleBack = () =>
    goBack(navigate, isPartnerWebviewSession() ? partnerHomePath() : "/");

  return (
    <header className="page-header">
      <button
        type="button"
        className="page-header__back"
        aria-label={String(t("back"))}
        onClick={handleBack}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M15 5l-7 7 7 7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <h1 className="page-header__title">{title}</h1>

      {/* Balances the back button so the title stays optically centred. */}
      <span className="page-header__spacer" aria-hidden="true" />
    </header>
  );
}
